'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';

import { TableHead, TableRow, TableWrapper } from '@/components';
import { Simplify } from '../../../prismicio-types';
import { TableContentVariant } from '@/types';

import styles from './FourColumnsTable.module.scss';

const headComponents = (variant: TableContentVariant): JSXMapSerializer => ({
  paragraph: ({ children }) => (
    <p className={`${styles.headText} ${styles[variant]}`}>{children}</p>
  ),
});

const headMobileComponents = (
  variant: TableContentVariant
): JSXMapSerializer => ({
  paragraph: ({ children }) => (
    <p
      className={`${styles.headText} ${styles.headMobileText} ${styles[variant]}`}
    >
      {children}
    </p>
  ),
});

const rowTitleComponents = (
  variant: TableContentVariant
): JSXMapSerializer => ({
  paragraph: ({ children }) => (
    <p className={`${styles.rowTitle} ${styles[variant]}`}>{children}</p>
  ),
});

const descriptionComponents = (
  variant: TableContentVariant
): JSXMapSerializer => ({
  paragraph: ({ children }) => (
    <p className={`${styles.description} ${styles[variant]}`}>{children}</p>
  ),
});

type Props = {
  data: Simplify<Content.PolicyTableSliceFourColumnsPrimary>;
  variant?: TableContentVariant;
};

export const FourColumnsTable: FC<Props> = ({ data, variant = 'page' }) => {
  return (
    <TableWrapper>
      <TableHead className={styles.head}>
        {data.head.map((item, index) => (
          <PrismicRichText
            field={item.title}
            key={`${item.title[0]?.type}-${index}`}
            components={headComponents(variant)}
          />
        ))}
      </TableHead>
      <TableWrapper>
        {data.body.map((item, index) => {
          const hasDescription = 'description' in item;
          const hasFirsValue = 'first_value' in item;
          const hasSecondValue = 'second_value' in item;

          return (
            <TableRow
              key={`${item.title[0]?.type}-${index}`}
              className={styles.row}
            >
              <div className={`${styles.box} ${styles[variant]}`}>
                <PrismicRichText
                  field={data.head[0]?.title}
                  key={`${item.title[0]?.type}-${index}`}
                  components={headMobileComponents(variant)}
                />
                <PrismicRichText
                  field={item.title}
                  components={rowTitleComponents(variant)}
                />
              </div>
              <div className={`${styles.box} ${styles[variant]}`}>
                <PrismicRichText
                  field={data.head[1]?.title}
                  key={`${item.title[1]?.type}-${index}`}
                  components={headMobileComponents(variant)}
                />
                {hasDescription && (
                  <div className={styles.descriptionWrapper}>
                    <PrismicRichText
                      field={item.description}
                      components={descriptionComponents(variant)}
                    />
                  </div>
                )}
              </div>
              <div
                className={`${styles.box} ${styles.values} ${styles[variant]}`}
              >
                <div className={styles.valueBox}>
                  <PrismicRichText
                    field={data.head[2]?.title}
                    key={`${item.title[2]?.type}-${index}`}
                    components={headMobileComponents(variant)}
                  />
                  {hasFirsValue && (
                    <p className={styles.valueWrapper}>
                      <span
                        className={`${styles.value} ${styles[item.first_value!]}`}
                      >
                        {item.first_value}
                      </span>
                    </p>
                  )}
                </div>
                <div className={styles.valueBox}>
                  <PrismicRichText
                    field={data.head[3]?.title}
                    key={`${item.title[3]?.type}-${index}`}
                    components={headMobileComponents(variant)}
                  />
                  {hasSecondValue && (
                    <p className={styles.valueWrapper}>
                      <span
                        className={`${styles.value} ${styles[item.second_value!]}`}
                      >
                        {item.second_value}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </TableRow>
          );
        })}
      </TableWrapper>
    </TableWrapper>
  );
};
